// web-v2/src/lib/quoteCompare.js
//
// QUOTE vs QUOTE. Where the offers on one deal differ FROM EACH OTHER.
//
// This is not off-applied-terms and must not be confused with it. Off-applied
// asks "did this carrier quote what I asked for?" — one quote against the
// application. It cannot answer the question that produced this module, because
// on Keith A Nichols / N8095M every quote matched the application exactly and
// it would have flagged nothing:
//
//   Beacon  $3,338   deductibles $2,500 NIM / $7,500 IM
//   Starr   $3,328   deductibles $0 / $0
//
// Ten dollars apart and materially different policies. A rank by premium puts
// them one line apart and says nothing, which is why THIS MODULE DECLARES NO
// WINNER. It shows the differences; the broker decides.
//
// TWO RULES IT MUST NOT BREAK, both learned the hard way:
//
//   1. NULL AND ZERO ARE DIFFERENT ANSWERS. The comparator it replaces
//      (offAppliedTerms' eqNum) returned "equal" whenever EITHER side was null,
//      so a missing value read as agreement — a guard that cannot fail when the
//      thing it guards is absent. A stated $0 deductible is the single best
//      feature of Starr's quote; "not stated" is the absence of an answer.
//      Conflating them is the prompt-81 defect, and it must not reappear inside
//      the tool built to compare them.
//
//   2. A MISSING FIELD IS A DIFFERENCE, NOT A SILENCE. If one quote states a
//      deductible and another does not, that is exactly the kind of gap the
//      broker needs to see before presenting, so it renders as a difference
//      with "not stated" on one side.
//
// Pure and dependency-free (no React, no DOM) so the node deploy gate can
// import it, same contract as quoteFields.js.

// ── value normalisation ─────────────────────────────────────────────────────
//
// Deductibles are TEXT columns holding '0', '0.00', '$0', '2500' or '5%'.
// Premiums and limits are numerics. Comparison happens on a NORMALISED form so
// '0' and '0.00' compare equal (they are the same answer spelled two ways)
// while '0' and NULL never do.

export const NOT_STATED = Symbol('not_stated');

/** NOT_STATED for null/undefined/'', otherwise a comparable primitive. */
export function normalizeValue(v) {
  if (v === null || v === undefined) return NOT_STATED;
  if (typeof v === 'string' && v.trim() === '') return NOT_STATED;
  if (typeof v === 'boolean') return v;
  const s = String(v).trim();
  const pct = s.match(/^(\d+(?:\.\d+)?)\s*%$/);
  if (pct) return `${Number(pct[1])}%`;
  const n = Number(s.replace(/[,$\s]/g, ''));
  // Number('') is 0, which is why the empty check above runs first.
  if (Number.isFinite(n)) return n;
  return s.toLowerCase();
}

export function isStated(v) {
  return normalizeValue(v) !== NOT_STATED;
}

/**
 * Do two raw values represent the same answer?
 * NOT_STATED equals only NOT_STATED — never a zero, never anything else.
 */
export function sameAnswer(a, b) {
  return normalizeValue(a) === normalizeValue(b);
}

// ── the fields compared ─────────────────────────────────────────────────────
//
// `kind` drives formatting only. Order is the order a broker reads a quote in:
// what it costs, then what it costs when something happens, then what it
// covers, then how long for.

export const DIFF_FIELDS = [
  { key: 'total_premium', label: 'Premium', kind: 'money' },
  { key: 'estimated_taxes', label: 'Estimated taxes', kind: 'money' },
  { key: 'deductible_not_in_motion', label: 'Deductible NIM', kind: 'deductible' },
  { key: 'deductible_in_motion', label: 'Deductible IM', kind: 'deductible' },
  { key: 'coverage_type', label: 'Coverage basis', kind: 'text' },
  { key: 'hull_value', label: 'Hull value', kind: 'money' },
  { key: 'liability_per_occurrence', label: 'Liability per occurrence', kind: 'money', alt: 'liability_csl' },
  { key: 'liability_per_passenger', label: 'Liability per passenger', kind: 'money', alt: 'per_passenger' },
  { key: 'medical_payments', label: 'Medical payments', kind: 'money', alt: 'medical_per_person' },
  { key: 'liability_smooth', label: 'Smooth limit', kind: 'bool' },
  { key: 'named_pilots_only', label: 'Named pilots only', kind: 'bool' },
  { key: 'term_months', label: 'Term (months)', kind: 'number' },
  { key: 'tria_status', label: 'TRIA', kind: 'text' },
  { key: 'tria_premium', label: 'TRIA premium', kind: 'money' },
];

/** The value for a field, honouring the alt column some options use instead. */
export function fieldValue(option, field) {
  if (!option) return null;
  const primary = option[field.key];
  if (isStated(primary)) return primary;
  if (field.alt && isStated(option[field.alt])) return option[field.alt];
  // Return the primary as-is (null/'' ) so "not stated" stays distinguishable.
  return primary === undefined ? null : primary;
}

/**
 * Build the comparison.
 *
 * @param {Array} columns [{ id, carrier, label, option }] — one per option being
 *        compared. The CALLER decides which options are in scope; this module
 *        never filters, ranks or recommends.
 * @returns {{ columns, rows }} where each row is
 *        { key, label, kind, differs, values: [{ raw, stated }] }
 */
export function buildQuoteComparison(columns) {
  const cols = Array.isArray(columns) ? columns.filter(Boolean) : [];
  const rows = DIFF_FIELDS.map((field) => {
    const values = cols.map((c) => {
      const raw = fieldValue(c.option, field);
      return { raw, stated: isStated(raw) };
    });
    // A row differs when any two columns disagree. "Stated vs not stated" IS a
    // disagreement — that is rule 2 above, and it is why this compares
    // normalised values rather than testing for presence first.
    let differs = false;
    for (let i = 1; i < values.length; i += 1) {
      if (!sameAnswer(values[0].raw, values[i].raw)) { differs = true; break; }
    }
    return { key: field.key, label: field.label, kind: field.kind, differs, values };
  });
  return { columns: cols, rows };
}

/** Rows that actually differ. Everything else is agreement and reads as noise. */
export function differingRows(diff) {
  return (diff && diff.rows ? diff.rows : []).filter((r) => r.differs);
}
